import { z } from 'zod';

/**
 * Wave 96 — Cotação Comparativa de Fornecedores.
 *
 * Multi-supplier price comparison sitting between a purchase_request
 * and a purchase_order. A round is opened with a list of items, then
 * one response slot is created per invited supplier. Each supplier
 * fills in their unit prices; the UI highlights min-price per row and
 * the overall winner. PMs convert the winning response into a real PO.
 */

export const quotationItemSchema = z.object({
  description: z.string().min(1).max(240),
  quantity: z.number().positive(),
  unit: z.string().min(1).max(20),
  // Wave 379R · link de volta pra linha do orçamento. Quando o PM
  // "puxa do orçamento" no NewRoundForm, este id fica gravado no
  // item da cotação. Propaga pra resposta do fornecedor e, ao
  // adjudicar o vencedor, vai parar como `budget_item_id` no PO —
  // assim o orçamento vê o realizado SEM o PM precisar reapropriar.
  budgetItemId: z.string().nullable().optional(),
});
export type QuotationItem = z.infer<typeof quotationItemSchema>;

export const quotationResponseItemSchema = quotationItemSchema.extend({
  unitPriceCents: z.number().int().nonnegative(),
  totalCents: z.number().int().nonnegative(),
});
export type QuotationResponseItem = z.infer<typeof quotationResponseItemSchema>;

export const quotationRoundStatusSchema = z.enum(['open', 'closed', 'converted']);
export type QuotationRoundStatus = z.infer<typeof quotationRoundStatusSchema>;

export const quotationResponseStatusSchema = z.enum(['pending', 'submitted', 'rejected']);
export type QuotationResponseStatus = z.infer<typeof quotationResponseStatusSchema>;

export interface QuotationResponse {
  id: string;
  roundId: string;
  supplierId: string;
  supplierName: string;
  status: QuotationResponseStatus;
  items: QuotationResponseItem[];
  totalCents: number | null;
  notes: string | null;
  submittedAt: string | null;
  createdAt: string;
  updatedAt: string;
  // W126.2 · condições estruturadas (NULL pra respostas pré-W126.2).
  paymentTerm?: PaymentTerm | null;
  freightTerm?: FreightTerm | null;
}

export interface QuotationRound {
  id: string;
  projectId: string;
  purchaseRequestId: string | null;
  title: string;
  items: QuotationItem[];
  status: QuotationRoundStatus;
  deadlineAt: string | null;
  notes: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  responseCount?: number;
  responses?: QuotationResponse[];
}

export const createQuotationRoundSchema = z.object({
  projectId: z.string().min(1),
  purchaseRequestId: z.string().min(1).nullable().optional(),
  title: z.string().min(2).max(160),
  items: z.array(quotationItemSchema).min(1).max(50),
  deadlineAt: z.string().datetime().nullable().optional(),
  notes: z.string().max(1000).nullable().optional(),
});
export type CreateQuotationRoundRequest = z.infer<typeof createQuotationRoundSchema>;

export const inviteSupplierSchema = z.object({
  supplierId: z.string().min(1),
});
export type InviteSupplierRequest = z.infer<typeof inviteSupplierSchema>;

/**
 * W126.2 · condições estruturadas (pagamento + frete) que antes
 * desciam concatenadas em `notes`. Frontend público em
 * `QuotationResponsePublicPage.tsx` já envia esses campos no payload;
 * sem este schema, o Zod strip-silencioso descartava antes de chegar
 * no service. Agora viram colunas dedicadas em `quotation_responses`
 * e ficam parseáveis pra filtro/analytics.
 *
 * Optional pra back-compat com clientes legados que ainda não enviam
 * (ex.: agente IA pré-W125.6). Service trata `undefined` como NULL.
 *
 * Valores casam EXATAMENTE com o que a UI emite (hyphenated, não
 * underscore — `a-vista`/`30d`/etc); qualquer renomeação precisa
 * acontecer aqui e no `PAYMENT_OPTIONS`/`FREIGHT_OPTIONS` do front
 * num PR só.
 */
export const PAYMENT_TERMS = ['a-vista', '30d', '60d', '90d', 'combinar'] as const;
export const FREIGHT_TERMS = ['cif', 'fob', 'proprio', 'combinar'] as const;
export type PaymentTerm = (typeof PAYMENT_TERMS)[number];
export type FreightTerm = (typeof FREIGHT_TERMS)[number];

export const submitQuotationResponseSchema = z.object({
  items: z.array(quotationResponseItemSchema).min(1),
  notes: z.string().max(1000).nullable().optional(),
  paymentTerm: z.enum(PAYMENT_TERMS).optional(),
  freightTerm: z.enum(FREIGHT_TERMS).optional(),
});
export type SubmitQuotationResponseRequest = z.infer<typeof submitQuotationResponseSchema>;

export const convertQuotationSchema = z.object({
  responseId: z.string().min(1),
});
export type ConvertQuotationRequest = z.infer<typeof convertQuotationSchema>;

/**
 * Wave 379T · adjudicação per-line. PM mapeia cada linha (índice no
 * round.items_json) para a resposta vencedora — pode ser fornecedor
 * diferente por linha. Server agrupa por responseId e cria N POs (um
 * por fornecedor vencedor). Linhas sem award ficam fora; o item volta
 * pro Backlog do orçamento (to_purchase=1) automaticamente.
 */
export const adjudicatePerLineSchema = z.object({
  awards: z
    .array(
      z.object({
        itemIndex: z.number().int().nonnegative(),
        responseId: z.string().min(1),
      }),
    )
    .min(1),
});
export type AdjudicatePerLineRequest = z.infer<typeof adjudicatePerLineSchema>;

export interface AdjudicatePerLineResult {
  purchaseOrderIds: string[];
  itemsLeftInBacklog: number;
  // Wave 379BV · #109 procurement esteira fatia 2/2 — IDs dos PR drafts
  // criados/atualizados em paralelo aos POs (audit/intent).
  // Pode ter menos itens que `purchaseOrderIds` se algum upsert falhou
  // (best-effort, não derruba a adjudicação).
  purchaseRequestDraftIds?: string[];
  // Wave Bid-Hardening · X-4 · 2026-05-11
  // Warnings de estouro de orçamento detectados na adjudicação.
  // Não bloqueia: PM ainda pode prosseguir. UI mostra alerta amarelo.
  budgetWarnings?: BudgetWarning[];
}

export interface BudgetWarning {
  kind: 'budget_overrun_total' | 'budget_overrun_item';
  /** Identifica o escopo do warning: 'total' (soma geral) ou budget_item_id. */
  budgetItemId: string | null;
  /** Nome amigável (ref + description) ou "Total do projeto". */
  label: string;
  plannedCents: number;
  awardedCents: number;
  overrunCents: number;
  overrunPct: number;
}
